import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";

const ChatUserDetailSection = ({ username }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRooms, setExpandedRooms] = useState({});

  // Confirmation modal state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    action: null,
    showDeleteMessages: false,
    deleteMessages: false,
  });

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const fetchUserData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatguests/by-username/${encodeURIComponent(username)}`
      );
      setUserData(response.data);
      // Auto-expand the first room
      if (response.data.messagesByRoom) {
        const roomKeys = Object.keys(response.data.messagesByRoom);
        if (roomKeys.length > 0) {
          setExpandedRooms({ [roomKeys[0]]: true });
        }
      }
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      setError(err.response?.data?.error || "Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const toggleRoom = (roomSlug) => {
    setExpandedRooms((prev) => ({
      ...prev,
      [roomSlug]: !prev[roomSlug],
    }));
  };

  const openConfirmDialog = (title, message, action, showDeleteMessages = false) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      action,
      showDeleteMessages,
      deleteMessages: false,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      action: null,
      showDeleteMessages: false,
      deleteMessages: false,
    });
  };

  const handleConfirmAction = async () => {
    const { action, deleteMessages } = confirmDialog;
    closeConfirmDialog();

    if (!userData?.guest) return;

    if (action === "ban") {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/chatguests/${userData.guest.id}/ban`,
          { is_banned: true, ban_reason: "Banned by admin" }
        );
        fetchUserData();
      } catch (err) {
        console.error("Failed to ban guest:", err);
      }
    } else if (action === "unban") {
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/chatguests/${userData.guest.id}/ban`,
          { is_banned: false, ban_reason: null }
        );
        fetchUserData();
      } catch (err) {
        console.error("Failed to unban guest:", err);
      }
    } else if (action === "delete") {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/admin/chatguests/${userData.guest.id}?deleteMessages=${deleteMessages}`
        );
        // Navigate back to guests list
        navigate("/admin/chatroom/guests");
      } catch (err) {
        console.error("Failed to delete guest:", err);
      }
    }
  };

  const handleBanToggle = () => {
    if (!userData?.guest) return;
    if (userData.guest.is_banned) {
      openConfirmDialog(
        "Unban User",
        `Are you sure you want to unban "${username}"?`,
        "unban"
      );
    } else {
      openConfirmDialog(
        "Ban User",
        `Are you sure you want to ban "${username}"? They will no longer be able to access the chatroom.`,
        "ban"
      );
    }
  };

  const handleDelete = () => {
    openConfirmDialog(
      "Delete User",
      `Are you sure you want to permanently delete "${username}"? This action cannot be undone.`,
      "delete",
      true
    );
  };

  const getAge = (birthYear) => {
    if (!birthYear) return "-";
    return new Date().getFullYear() - birthYear;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" sx={{ color: "#ef5350", mb: 2 }}>
          {error}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate("/admin/chatroom/guests")}
          sx={{ color: "#fff", borderColor: "#394867" }}
        >
          Back to Guests
        </Button>
      </Box>
    );
  }

  if (!userData?.guest) {
    return null;
  }

  const { guest, messagesByRoom, totalMessages } = userData;

  return (
    <Box>
      {/* Back Button and Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate("/admin/chatroom/guests")}
          sx={{ color: "#9e9e9e", borderColor: "#394867" }}
        >
          ← Back
        </Button>
        <Typography variant="h5" sx={{ color: "#fff", fontWeight: 600 }}>
          User: {username}
        </Typography>
        <IconButton
          onClick={fetchUserData}
          disabled={loading}
          sx={{
            color: "#9e9e9e",
            "&:hover": { color: "#fff", backgroundColor: "#394867" },
          }}
          title="Refresh"
        >
          <RefreshIcon
            sx={{
              animation: loading ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </IconButton>
        {guest.is_claimed && (
          <Chip
            label="Claimed"
            size="small"
            sx={{ backgroundColor: "#1565c0", color: "#fff" }}
          />
        )}
        {guest.is_banned && (
          <Chip
            label="Banned"
            size="small"
            sx={{ backgroundColor: "#b71c1c", color: "#fff" }}
          />
        )}
      </Box>

      {/* User Info Card */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 0.5 }}>User Information</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "#666" }}>ID:</Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>{guest.id}</Typography>

              <Typography variant="body2" sx={{ color: "#666" }}>Gender:</Typography>
              <Typography variant="body2" sx={{ color: "#fff", textTransform: "capitalize" }}>{guest.gender || "-"}</Typography>

              <Typography variant="body2" sx={{ color: "#666" }}>Age:</Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>{getAge(guest.birth_year)}</Typography>

              <Typography variant="body2" sx={{ color: "#666" }}>Location:</Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>{guest.location_city || guest.location_zip || "-"}</Typography>

              <Typography variant="body2" sx={{ color: "#666" }}>Message Count:</Typography>
              <Typography variant="body2" sx={{ color: "#4caf50", fontWeight: 600 }}>{guest.message_count || 0}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 0.5 }}>Activity</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "#666" }}>Created:</Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>{formatDate(guest.created_at)}</Typography>

              <Typography variant="body2" sx={{ color: "#666" }}>Last Seen:</Typography>
              <Typography variant="body2" sx={{ color: "#fff" }}>{formatDate(guest.last_seen_at)}</Typography>

              <Typography variant="body2" sx={{ color: "#666" }}>IP Address:</Typography>
              <Typography variant="body2" sx={{ color: "#fff", fontFamily: "monospace", fontSize: "0.8rem" }}>{guest.ip_address || "-"}</Typography>

              <Typography variant="body2" sx={{ color: "#666" }}>User Agent:</Typography>
              <Typography variant="body2" sx={{ color: "#fff", fontSize: "0.75rem", wordBreak: "break-all" }}>{guest.user_agent || "-"}</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, mt: 3, pt: 2, borderTop: "1px solid #394867" }}>
          <Button
            variant="contained"
            onClick={handleBanToggle}
            sx={{
              backgroundColor: guest.is_banned ? "#4caf50" : "#ef5350",
              "&:hover": { backgroundColor: guest.is_banned ? "#388e3c" : "#d32f2f" },
            }}
            startIcon={<BlockIcon />}
          >
            {guest.is_banned ? "Unban User" : "Ban User"}
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            sx={{
              backgroundColor: "#ff5722",
              "&:hover": { backgroundColor: "#e64a19" },
            }}
            startIcon={<DeleteIcon />}
          >
            Delete User
          </Button>
        </Box>
      </Paper>

      {/* Messages by Room */}
      <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
        Messages ({totalMessages})
      </Typography>

      {Object.keys(messagesByRoom).length === 0 ? (
        <Paper sx={{ p: 3, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "#9e9e9e" }}>
            No messages found
          </Typography>
        </Paper>
      ) : (
        Object.entries(messagesByRoom).map(([roomSlug, roomData]) => (
          <Paper key={roomSlug} sx={{ mb: 2, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
            {/* Room Header */}
            <Box
              onClick={() => toggleRoom(roomSlug)}
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                backgroundColor: "#0f1729",
                "&:hover": { backgroundColor: "#1a1a2e" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600 }}>
                  {roomData.roomName}
                </Typography>
                <Chip
                  label={`${roomData.messages.length} messages`}
                  size="small"
                  sx={{ backgroundColor: "#1565c0", color: "#fff", fontSize: "0.7rem" }}
                />
              </Box>
              {expandedRooms[roomSlug] ? (
                <ExpandLess sx={{ color: "#9e9e9e" }} />
              ) : (
                <ExpandMore sx={{ color: "#9e9e9e" }} />
              )}
            </Box>

            {/* Messages List */}
            <Collapse in={expandedRooms[roomSlug]}>
              <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                {roomData.messages.map((msg, idx) => (
                  <Box
                    key={msg.id}
                    sx={{
                      p: 2,
                      borderTop: idx > 0 ? "1px solid #394867" : "none",
                      opacity: msg.is_deleted ? 0.5 : 1,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
                        {formatDate(msg.created_at)}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {msg.is_deleted && (
                          <Chip label="Deleted" size="small" sx={{ height: 18, fontSize: "0.65rem", backgroundColor: "#b71c1c", color: "#fff" }} />
                        )}
                        {msg.moderation_status && msg.moderation_status !== "approved" && (
                          <Chip label={msg.moderation_status} size="small" sx={{ height: 18, fontSize: "0.65rem", backgroundColor: "#ff9800", color: "#fff" }} />
                        )}
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: msg.is_deleted ? "#666" : "#fff", wordBreak: "break-word" }}>
                      {msg.is_deleted ? <em>[Deleted: {msg.deleted_reason || "No reason"}]</em> : msg.content}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Paper>
        ))
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a2e",
            border: "1px solid #394867",
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", borderBottom: "1px solid #394867" }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ color: "#9e9e9e" }}>
            {confirmDialog.message}
          </DialogContentText>
          {confirmDialog.showDeleteMessages && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmDialog.deleteMessages}
                    onChange={(e) =>
                      setConfirmDialog((prev) => ({
                        ...prev,
                        deleteMessages: e.target.checked,
                      }))
                    }
                    sx={{
                      color: "#9e9e9e",
                      "&.Mui-checked": { color: "#ef5350" },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      Also delete all messages
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9e9e9e" }}>
                      If unchecked, messages will be anonymized (guest_id set to null)
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #394867" }}>
          <Button onClick={closeConfirmDialog} sx={{ color: "#9e9e9e" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            sx={{
              backgroundColor: confirmDialog.action === "unban" ? "#4caf50" : "#ef5350",
              "&:hover": {
                backgroundColor: confirmDialog.action === "unban" ? "#388e3c" : "#d32f2f",
              },
            }}
          >
            {confirmDialog.action === "ban" && "Ban User"}
            {confirmDialog.action === "unban" && "Unban User"}
            {confirmDialog.action === "delete" && "Delete User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatUserDetailSection;
